import { AddressEditModel } from "@shared/models/address-edit.model";

export const cityTypeaheadEndpoint = '/api/common/geographyapi/getCityTypeaheadResults?maxResults=15&query=';

export function highlightMatch(text: string, query: string): string {
     // Return original text if query is empty
  if (!query.trim()) return text;
  
  // First, try to highlight the exact phrase
  let result = text;
  const exactRegex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const exactMatches = result.match(exactRegex);
  
  if (exactMatches) {
    // If exact match found, highlight it
    result = result.replace(exactRegex, '<strong>$1</strong>');
  } else {
    // Otherwise, highlight individual words
    const queryWords = query.trim().split(/\s+/);
    const pattern = queryWords
      .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('|');
    
    const regex = new RegExp(`(${pattern})`, 'gi');
    result = result.replace(regex, '<strong>$1</strong>');
  }
  
  return result;
  }

export function hasExactCompanyNameMatch(inputName: string, companies: { name: string }[]): boolean {
  const normalizedInput = inputName.trim().toLowerCase();
  return companies.some(company => company.name.trim().toLowerCase() === normalizedInput);
}

export function  isFuzzyMatch(a: string, b: string): boolean {
    if (!a || !b) return false;

    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();

    if (aLower.includes(bLower) || bLower.includes(aLower)) {
      return true;
    }

    const aTokens = aLower.split(/\s+/);
    const bTokens = bLower.split(/\s+/);

    return aTokens.some(token => bTokens.includes(token));
  }

export function matchAddresses(a: AddressEditModel, b: AddressEditModel): number {
  if (!a || !b) return 0;

  let score = 0;
  let weight = 0;

  score += stringSimilarity(a.streetAddress, b.streetAddress) * 3; weight += 3;
  score += stringSimilarity(a.city, b.city) * 1; weight += 1;
  score += stringSimilarity(a.stateProvince, b.stateProvince) * 1; weight += 1;
  score += stringSimilarity(a.postalCode, b.postalCode) * 1; weight += 1;
  score += stringSimilarity(a.country!, b.country!) * 1; weight += 1;

  return score / weight;
}

function stringSimilarity(a?: string, b?: string): number {
  if (!a || !b) return 0;
  a = a.toLowerCase().trim();
  b = b.toLowerCase().trim();

  const distance = levenshteinDistance(a, b);
  const maxLength = Math.max(a.length, b.length);
  return maxLength === 0 ? 1 : 1 - distance / maxLength;
}

function levenshteinDistance(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));

  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,       
        dp[i][j - 1] + 1,       
        dp[i - 1][j - 1] + cost 
      );
    }
  }

  return dp[a.length][b.length];
}

export function filterNullishValues(address: AddressEditModel) {
  return [address.streetAddress, address.city, address.stateProvince, address.postalCode]
    .filter(part => part !== null && part !== undefined && part !== '' && part !== 'not available' && part !== 'test' && part !== 'n/a' && part !== 'null')
    .join(', ');
}

export function isAddressFieldMissing(value?: string | null): boolean {
  return !value || value === 'not available' || value === 'n/a' || value === 'test' || value.trim() === '';
}

export function getAddressValidationErrors(address: AddressEditModel): string[] {
  const formattedAddress = (address.fullAddress?.trim() || filterNullishValues(address)).trim();
  if (formattedAddress.length > 0) {
    return [];
  }

  const errors: string[] = [];
  if (isAddressFieldMissing(address.streetAddress)) errors.push('street');
  if (isAddressFieldMissing(address.city)) errors.push('city');
  if (isAddressFieldMissing(address.postalCode)) errors.push('postal');
  return errors;
}

export function addressValidationErrorsToFormErrors(errors: string[]): Record<string, boolean> | null {
  if (!errors.length) {
    return null;
  }

  return errors.reduce((acc, curr) => ({ ...acc, [curr]: true }), {});
}